import { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { Icon } from '@/components/icon';
import { ConversationJob } from '@/explore/types';

import {
  formatDuration,
  getJobDuration,
  getJobStartTime,
  getJobStatus,
  getJobStatusMessage,
} from '@/core/conversation/job';

import { NestedConversationJob } from '@/core/conversation/job';

import { ProgressList } from './progress-list';
import { StatusMessageContent } from './status-message-content';

import styles from './job-node.module.scss';

const channelIconMap = {
  notion: 'Notion' as const,
  slack: 'Slack' as const,
  dwh: 'Database' as const,
  google_drive: 'GoogleDrive' as const,
  confluence: 'Confluence' as const,
  custom_unstructured: 'File' as const,
};

const ChannelIcon = ({ channel }: { channel: string | undefined }) => {
  if (channel === undefined) {
    return null;
  }
  const iconName = channelIconMap[channel as keyof typeof channelIconMap];
  return iconName !== undefined ? (
    <div>
      <Icon name={iconName} size={14} />
    </div>
  ) : null;
};

interface JobPreviewProps {
  job: ConversationJob;
  onOpen: () => void;
}

const JobPreview = ({ job, onOpen }: JobPreviewProps) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<number | null>(null);

  const jobStatus = getJobStatus(job);
  const lastStatusMessage = getJobStatusMessage(job);
  const isCompleted = jobStatus === 'completed';
  const isError = jobStatus === 'error';
  const isRunning = jobStatus === 'running';

  // Live timer for running jobs
  useEffect(() => {
    if (!isRunning) {
      setElapsedTime(null);
      return;
    }

    const startTime = getJobStartTime(job);
    if (startTime === null) {
      return;
    }

    const updateElapsed = () => {
      setElapsedTime(Math.round((Date.now() - startTime) / 1000));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [isRunning, job]);

  const previousMessageRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      lastStatusMessage !== undefined &&
      previousMessageRef.current !== undefined &&
      lastStatusMessage !== previousMessageRef.current
    ) {
      setIsTransitioning(true);
    }
    previousMessageRef.current = lastStatusMessage;
  }, [lastStatusMessage]);

  const duration = getJobDuration(job);
  const displayDuration = isRunning ? elapsedTime : duration;
  const durationStr =
    displayDuration !== null && displayDuration > 0 ? formatDuration(displayDuration) : undefined;

  return (
    <div
      className={styles.preview}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}>
      <ChannelIcon channel={job.channel} />

      <div className={styles.statusMessageContainer}>
        {isTransitioning && (job.progress?.length ?? 0) > 0 && (
          <div
            className={classNames(styles.statusMessage, styles.slideUpOut)}
            key={`prev-${lastStatusMessage}`}>
            <StatusMessageContent content={lastStatusMessage} uniqueId={`${job.jobId}-prev`} />
          </div>
        )}
        <div
          className={classNames(styles.statusMessage, {
            [styles.slideUpIn]: isTransitioning,
          })}
          key={lastStatusMessage}
          onAnimationEnd={() => setIsTransitioning(false)}>
          <StatusMessageContent content={lastStatusMessage} uniqueId={job.jobId} />
        </div>
      </div>

      {durationStr !== undefined && <span className={styles.duration}>{durationStr}</span>}
      {isCompleted && <Icon name="Check" className={styles.completedCheck} size={12} />}
      {isError && <Icon name="X" className={styles.errorIcon} size={12} />}
    </div>
  );
};

interface JobNodeProps {
  job: NestedConversationJob;
  depth: number;
  onToggle: () => void;
}

export const JobNode = ({ job, depth, onToggle }: JobNodeProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles.jobNode} onClick={onToggle}>
      {isOpen ? (
        <ProgressList progress={job.progress} onClose={() => setIsOpen(false)} />
      ) : (
        <JobPreview job={job} onOpen={() => setIsOpen(true)} />
      )}
      {job.children.length > 0 && (
        <div className={styles.childJobs} onClick={(e) => e.stopPropagation()}>
          {job.children.map((childJob) => (
            <JobNode key={childJob.jobId} job={childJob} depth={depth + 1} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
};
